/**
 * AdminShell — section layout for every admin/* screen.
 *
 * Replaces the chrome + auth-guard logic that used to live in
 * app/admin/_layout.tsx.  Individual admin screens are wrapped in this
 * component at registration time (see RootNavigator) so each admin route
 * renders with the sidebar and auth gate around it.
 *
 * Behavior:
 *  - If not authenticated as an admin (or currently logging out), renders
 *    null.  AuthContext.logout() handles the redirect to '/'.
 *  - On desktop-web (width >= MOBILE_BREAKPOINT) the sidebar sits to the
 *    left of the screen content.  On narrower viewports it stacks above.
 */
import React, { ReactNode } from 'react';
import { View } from 'react-native';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useResponsive } from '../utils/responsive';
import AdminSidebar from '../components/AdminSidebar';
import ContentContainer from '../components/ContentContainer';

const MOBILE_BREAKPOINT = 768;

export default function AdminShell({ children }: { children: ReactNode }) {
  const { isAuthenticated, role, isLoading, isLoggingOut } = useAuth();
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < MOBILE_BREAKPOINT;

  if (!isLoading && (!isAuthenticated || role !== 'admin' || isLoggingOut)) return null;

  return (
    <View
      style={{
        flex: 1,
        flexDirection: isMobile ? 'column' : 'row',
        backgroundColor: colors.background,
      }}
    >
      <AdminSidebar />
      <View style={{ flex: 1 }}>
        {/* footer=false: the shell wraps the screen's scroll view, so a footer
            here pins to the viewport bottom. Screens render PageFooter at
            their own scroll end (inner ContentContainer / ListFooterComponent). */}
        <ContentContainer role="admin" footer={false}>{children}</ContentContainer>
      </View>
    </View>
  );
}
